import React from "react";
import PropTypes from "prop-types";

// fmtSmape formats a sMAPE value (range [0,2]) as a percentage string.
// Returns "—" for null/undefined.
function fmtSmape(smape) {
  if (smape == null) return "—";
  return (smape * 100).toFixed(1) + "%";
}

// STATUS_NOTE maps non-ok statuses to a short human-readable note.
const STATUS_NOTE = {
  failed: "failed",
  insufficient_history: "insufficient history",
};

// BacktestLadder renders a ranked list of algorithm candidates for one series.
// Candidates are sorted by sMAPE ascending (nulls last). The winner is highlighted.
// Bar widths are proportional to sMAPE (longer = worse); heights use inline style
// because this project overrides Tailwind's height scale (h-* classes render 0px).
export default function BacktestLadder({ ladder }) {
  if (!ladder || ladder.length === 0) return null;

  // Sort: ok first (ascending smape), then failed/insufficient last
  const sorted = [...ladder].sort((a, b) => {
    const aVal = a.status === "ok" && a.smape != null ? a.smape : Infinity;
    const bVal = b.status === "ok" && b.smape != null ? b.smape : Infinity;
    return aVal - bVal;
  });

  const maxSmape = sorted.reduce((max, r) => {
    if (r.smape != null) return Math.max(max, r.smape);
    return max;
  }, 0);

  return (
    <div className="px-4 pb-3 pt-2">
      <div className="flex flex-col gap-1.5">
        {sorted.map((row) => {
          const isWinner = !!row.is_winner;
          const barWidthPct =
            maxSmape > 0 && row.smape != null
              ? Math.round((row.smape / maxSmape) * 100)
              : 0;
          const statusNote = STATUS_NOTE[row.status] || null;

          return (
            <div
              key={row.algorithm}
              className={`flex items-center gap-3 rounded-ctrl px-2 py-1.5 ${
                isWinner ? "bg-success-soft" : "bg-surface-2"
              }`}
            >
              {/* Algorithm name — fixed width via inline style so every bar starts
                  at the same x (this project overrides Tailwind's w-* scale). */}
              <span
                className={`shrink-0 text-xs font-medium ${isWinner ? "text-success" : "text-text-2"}`}
                style={{ width: 96 }}
              >
                {row.algorithm}
                {isWinner && (
                  <span className="ml-1 text-success" aria-label="winner">
                    ★
                  </span>
                )}
              </span>

              {/* sMAPE value — fixed width so the bars line up in a column */}
              <span
                className={`shrink-0 text-right text-xs tabular-nums ${
                  isWinner ? "text-success" : "text-text-2"
                }`}
                style={{ width: 52 }}
              >
                {fmtSmape(row.smape)}
              </span>

              {/* Bar track */}
              <div className="flex-1 overflow-hidden rounded-full bg-surface-2" style={{ height: 8 }}>
                {barWidthPct > 0 && (
                  <div
                    style={{
                      width: `${barWidthPct}%`,
                      height: 8,
                      backgroundColor: isWinner ? "hsl(var(--success))" : "hsl(var(--chart-pred))",
                      borderRadius: "9999px",
                    }}
                  />
                )}
              </div>

              {/* Status note for non-ok rows */}
              {statusNote && (
                <span className="shrink-0 text-xs text-text-3 italic">{statusNote}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

BacktestLadder.propTypes = {
  ladder: PropTypes.arrayOf(
    PropTypes.shape({
      algorithm: PropTypes.string.isRequired,
      smape: PropTypes.number,
      status: PropTypes.string.isRequired,
      is_winner: PropTypes.bool,
    })
  ).isRequired,
};
